import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Send, Clock, MessageCircle } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => { api.get("/contact-info").then((r) => setInfo(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message envoyé !");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const rows = [];
  if (info) {
    if (info.email) rows.push({ icon: Mail, v: info.email, href: `mailto:${info.email}` });
    if (info.phone) rows.push({ icon: Phone, v: info.phone, href: `tel:${info.phone.replace(/\s/g, "")}` });
    if (info.whatsapp) rows.push({ icon: MessageCircle, v: info.whatsapp, href: `https://wa.me/${info.whatsapp.replace(/[^0-9]/g, "")}` });
    if (info.address) rows.push({ icon: MapPin, v: info.address });
    if (info.hours) rows.push({ icon: Clock, v: info.hours });
    (info.extras || []).forEach((ex) => { if (ex.value) rows.push({ icon: MapPin, v: ex.label ? `${ex.label} : ${ex.value}` : ex.value }); });
  }

  return (
    <div className="section-sand min-h-screen py-16">
      <div className="max-w-5xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <h1 className="font-heading font-extrabold text-4xl text-ink">{t("nav.contact")}</h1>
          <p className="text-muted2 font-body mt-3">Une question ? Notre équipe vous répond sous 24h.</p>
          <div className="space-y-4 mt-8" data-testid="contact-info-list">
            {rows.map((c, i) => (
              <div key={i} data-testid={`contact-info-${i}`} className="flex items-center gap-3 text-ink font-body">
                <div className="w-11 h-11 rounded-xl bg-canard/10 flex items-center justify-center"><c.icon className="w-5 h-5 text-canard" /></div>
                {c.href ? <a href={c.href} className="font-semibold hover:text-canard transition-colors">{c.v}</a> : <span className="font-semibold">{c.v}</span>}
              </div>
            ))}
          </div>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl shadow-card border border-border p-6 sm:p-8 space-y-4">
          <Input data-testid="contact-name" required placeholder={t("auth.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl bg-sand" />
          <Input data-testid="contact-email" required type="email" placeholder={t("checkout.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl bg-sand" />
          <Input data-testid="contact-subject" required placeholder="Sujet" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="h-11 rounded-xl bg-sand" />
          <Textarea data-testid="contact-message" required placeholder="Votre message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-xl bg-sand min-h-32" />
          <Button type="submit" data-testid="contact-submit" className="w-full h-11 rounded-full bg-emeraude hover:bg-emeraude-dark shadow-cta">
            <Send className="w-4 h-4 mr-2" /> Envoyer
          </Button>
          {sent && <p className="text-emeraude-dark text-sm font-semibold text-center">✓ Merci, nous revenons vers vous rapidement.</p>}
        </form>
      </div>
    </div>
  );
}
